<template>
  <div class="sofia">
    <!-- Botón flotante -->
    <button
      class="sofia_bubble"
      :class="{ hidden: open }"
      @click="toggle"
      aria-label="Abrir chat con Sofía"
    >
      <i class="icon-comment"></i>
      <span class="sofia_bubble_label">{{ t.sofia.bubble }}</span>
    </button>

    <!-- Panel de chat -->
    <div class="sofia_panel" :class="{ open: open }">
      <div class="sofia_head">
        <div class="sofia_avatar">S</div>
        <div class="sofia_who">
          <strong>Sofía</strong>
          <span>{{ t.sofia.role }}</span>
        </div>
        <button class="sofia_close" @click="toggle" aria-label="Cerrar">
          <i class="icon-cancel"></i>
        </button>
      </div>

      <div class="sofia_body" ref="body">
        <div
          v-for="(m, i) in messages"
          :key="i"
          class="sofia_msg"
          :class="m.role"
        >
          <div class="bubble">{{ m.content }}</div>
        </div>
        <div v-if="loading" class="sofia_msg assistant">
          <div class="bubble typing"><span></span><span></span><span></span></div>
        </div>
        <div v-if="error" class="sofia_error">{{ error }}</div>
      </div>

      <form class="sofia_foot" @submit.prevent="send">
        <input
          v-model="draft"
          type="text"
          :placeholder="t.sofia.placeholder"
          :disabled="loading"
          autocomplete="off"
        />
        <button type="submit" :disabled="loading || !draft.trim()">
          <i class="icon-paper-plane"></i>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { i18n } from "@/i18n";

// URL del backend de Sofía. Por defecto usamos una ruta RELATIVA (mismo
// origen): el navegador llama a https://<dominio>/api/sofia/web/chat y el
// nginx de este contenedor hace proxy hacia finanzas-api-modular:3001 por la
// red interna de Docker. Así el backend nunca se publica ni se expone, y se
// evita el bloqueo de mixed-content (http dentro de https).
const SOFIA_API =
  process.env.VUE_APP_SOFIA_API || "/api/sofia/web/chat";

const SESSION_KEY = "datamind_sofia_session";
const PHONE_KEY = "datamind_sofia_phone";

export default {
  name: "SofiaChat",
  data() {
    return {
      open: false,
      draft: "",
      loading: false,
      error: "",
      messages: [],
      sessionId: localStorage.getItem(SESSION_KEY) || null,
      phone: localStorage.getItem(PHONE_KEY) || null,
    };
  },
  computed: {
    t() {
      return i18n.m;
    },
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open && this.messages.length === 0) {
        this.messages.push({ role: "assistant", content: this.t.sofia.greeting });
      }
      if (this.open) this.scrollDown();
    },

    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.body;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    async send() {
      const text = this.draft.trim();
      if (!text || this.loading) return;

      this.messages.push({ role: "user", content: text });
      this.draft = "";
      this.error = "";
      this.loading = true;
      this.scrollDown();

      try {
        const res = await fetch(SOFIA_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: text,
            sessionId: this.sessionId,
            phone: this.phone,
          }),
        });

        // El rate limit (429) no es un fallo de conexión: merece su propio
        // mensaje. Antes cualquier error HTTP decía "no pude conectar", lo que
        // hacía imposible entender qué pasaba.
        if (res.status === 429) {
          this.error = this.t.sofia.rateLimit;
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        // Persistimos la sesión y el teléfono: así Sofía mantiene el hilo
        // y puede reconocer al mismo usuario luego en WhatsApp.
        if (data.sessionId) {
          this.sessionId = data.sessionId;
          localStorage.setItem(SESSION_KEY, data.sessionId);
        }
        if (data.phone) {
          this.phone = data.phone;
          localStorage.setItem(PHONE_KEY, data.phone);
        }

        this.messages.push({
          role: "assistant",
          content: data.reply || this.t.sofia.fallback,
        });
      } catch (e) {
        this.error = this.t.sofia.error;
      } finally {
        this.loading = false;
        this.scrollDown();
      }
    },
  },
};
</script>

<style scoped>
.sofia {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 60;
  font-family: "Poppins", sans-serif;
}

/* ── Botón flotante ── */
.sofia_bubble {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  border: none;
  border-radius: 50px;
  background: var(--main-color);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(33, 118, 255, 0.35);
  transition: all 0.3s ease;
}
.sofia_bubble:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 36px rgba(33, 118, 255, 0.45);
}
.sofia_bubble.hidden {
  opacity: 0;
  pointer-events: none;
  transform: scale(0.8);
}
.sofia_bubble i {
  font-size: 18px;
}

/* ── Panel ── */
.sofia_panel {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 380px;
  max-width: calc(100vw - 40px);
  height: 540px;
  max-height: calc(100vh - 100px);
  background: #fff;
  border: 1px solid var(--dm-border, #e2eaf6);
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(13, 27, 58, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  opacity: 0;
  visibility: hidden;
  transform: translateY(20px) scale(0.96);
  transition: all 0.3s ease;
}
.sofia_panel.open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
}

.sofia_head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  background: var(--dm-navy, #0d1b3a);
  color: #fff;
}
.sofia_avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--main-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}
.sofia_who {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  flex: 1;
}
.sofia_who span {
  font-size: 12px;
  opacity: 0.7;
}
.sofia_close {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  opacity: 0.7;
  font-size: 16px;
}
.sofia_close:hover {
  opacity: 1;
}

.sofia_body {
  flex: 1;
  overflow-y: auto;
  padding: 18px;
  background: var(--dm-surface, #f5f8fd);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sofia_msg {
  display: flex;
}
.sofia_msg.user {
  justify-content: flex-end;
}
.sofia_msg .bubble {
  max-width: 80%;
  padding: 11px 15px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
}
.sofia_msg.assistant .bubble {
  background: #fff;
  color: var(--dm-navy, #0d1b3a);
  border: 1px solid var(--dm-border, #e2eaf6);
  border-bottom-left-radius: 4px;
}
.sofia_msg.user .bubble {
  background: var(--main-color);
  color: #fff;
  border-bottom-right-radius: 4px;
}

.typing {
  display: flex;
  gap: 4px;
  align-items: center;
}
.typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9db4d8;
  animation: blink 1.2s infinite;
}
.typing span:nth-child(2) {
  animation-delay: 0.2s;
}
.typing span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes blink {
  0%,
  60%,
  100% {
    opacity: 0.25;
  }
  30% {
    opacity: 1;
  }
}

.sofia_error {
  font-size: 13px;
  color: #c0392b;
  text-align: center;
}

.sofia_foot {
  display: flex;
  gap: 8px;
  padding: 12px;
  background: #fff;
  border-top: 1px solid var(--dm-border, #e2eaf6);
}
.sofia_foot input {
  flex: 1;
  border: 1px solid var(--dm-border, #e2eaf6);
  border-radius: 50px;
  padding: 10px 16px;
  font-size: 14px;
  color: var(--dm-navy, #0d1b3a);
  background: var(--dm-surface, #f5f8fd);
  outline: none;
}
.sofia_foot input:focus {
  border-color: var(--main-color);
}
.sofia_foot button {
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: var(--main-color);
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
}
.sofia_foot button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .sofia {
    right: 16px;
    bottom: 16px;
  }
  .sofia_bubble_label {
    display: none;
  }
  .sofia_bubble {
    padding: 16px;
  }
}
</style>
